import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Container,
  Backdrop,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Badge,
  Alert,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  Avatar,
  LinearProgress,
  Stack,
  AppBar,
  Toolbar,
} from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  Launch as LaunchIcon,
  CloudUpload as CloudUploadIcon,
  VideoLibrary as VideoLibraryIcon,
  Schedule as ScheduleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Warning as WarningIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "react-query";
import client from "../../../API";
import { useCreator } from "../../../Hooks/agencySelectedCreatorContext";
import CreatorTimeline from "../../../CreatorUser/CreatorPages/Dashboard/creatorcomponents/creatorTimeline";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import ChatScreen from "../../../Components/ChatScreen";
import {
  formatMessageContent,
  TimelineStatus,
} from "../../../Utils/constants";
import PageTitle from "../../../Components/PageTitle";
import AgencyCampaignPitchDialog from "./agencyCampaignPitchDialog";

// Constants for mobile layout
const MOBILE_HEADER_HEIGHT = 56;
const APPSHELL_BAR_HEIGHT = 86;
const APPSHELL_EXTRA_GUTTER = 12;

// Utility function to format the due date
function formatIdealDueDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().substr(-2);
  return `${month}/${day}/${year}`;
}

// Status tooltip messages
const getStatusTooltip = (status) => {
  const statusTooltips = {
    Draft: "Campaign is being prepared - not yet launched",
    Launched: "Campaign is live and active",
    Completed: "Campaign has been finished and finalized",
    Pitched: "Initial outreach sent - waiting for response",
    Counter: "Counter-offer made - waiting for brand response",
    Accepted: "Terms accepted and campaign deliverables in progress",
    Approved: "Content approved - campaign deliverables complete",
    Declined: "Declined to participate in this campaign",
    Dropped: "Removed from the campaign",
    Pending: "Waiting for action or approval",
  };
  return statusTooltips[status] || `Current status: ${status}`;
};

// Progress calculation for individual videos
const calculateVideoProgress = (videoData, crosspostData = []) => {
  const steps = [
    {
      id: "created",
      label: "Assignment",
      description: "Video assignment created",
      completed: true,
    },
    {
      id: "submission",
      label: "Content Submitted",
      description: "Draft/content uploaded",
      completed: !!(videoData.submissionLink || videoData.draftLink),
    },
    {
      id: "live",
      label: "Live Link",
      description: "Published content link added",
      completed: !!videoData.liveLink,
    },
    {
      id: "boost",
      label: "Boost Code",
      description: "Promotional code provided",
      completed: !!videoData.boostCode,
    },
    {
      id: "crossposts",
      label: "Crossposts",
      description: "All crosspost links completed",
      completed:
        crosspostData.length === 0 ||
        crosspostData.every((c) => c.liveLink && c.boostCode),
    },
    {
      id: "payment",
      label: "Payment",
      description: "Payment processed",
      completed: !!videoData.payoutComplete,
    },
  ];

  const completedSteps = steps.filter((step) => step.completed);
  const progressPercentage = (completedSteps.length / steps.length) * 100;

  return {
    steps,
    completedSteps: completedSteps.length,
    totalSteps: steps.length,
    progressPercentage,
    status:
      progressPercentage === 100
        ? "completed"
        : progressPercentage > 0
        ? "in-progress"
        : "not-started",
    nextStep: steps.find((step) => !step.completed)?.label || null,
  };
};

// Main container that adapts to screen size
const MainContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  maxWidth: "none !important",
  [theme.breakpoints.down("lg")]: {
    height: "calc(var(--vh, 1vh) * 100)",
    maxHeight: "calc(var(--vh, 1vh) * 100)",
    overflow: "hidden",
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(2),
    height: "calc(100vh - 32px)",
  },
}));

// Desktop layout container
const DesktopChatContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("lg")]: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(2),
    overflow: "hidden",
  },
}));

// Mobile layout container
const MobileChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}));

// Desktop sidebar — no CardContent wrapper needed, renderCampaignInfo goes directly inside
const DesktopSidebar = styled(Card)(({ theme }) => ({
  minWidth: 380,
  maxWidth: 420,
  height: "100%",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
}));

// Desktop chat area — flex column so children can fill height
const DesktopChatCard = styled(Card)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  minWidth: 0,
}));

// Mobile accordion for campaign info
const MobileCampaignAccordion = styled(Accordion)(({ theme }) => ({
  margin: 0,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
  },
  "&.chat-mode-hidden": {
    display: "none",
  },
}));

// Mobile scrollable content area
const MobileScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.text.primary, 0.2),
    borderRadius: 2,
  },
}));

// Fullscreen chat container for mobile
const MobileFullscreenChat = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: theme.zIndex.modal - 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

// Mobile chat header
const MobileChatHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  minHeight: MOBILE_HEADER_HEIGHT,
  flexShrink: 0,
}));

// Info section with better spacing control
const InfoSection = styled(Box)(({ theme, compact = false }) => ({
  padding: compact ? theme.spacing(1.5) : theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
  "& + &": {
    borderTop: "none",
  },
}));

// Compact section for nested content
const CompactSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  "&:not(:last-child)": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

// FIX: flex:1 + minHeight:0 allows this to fill remaining card height
const ChatContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
});

// Campaign Brief Card
const CampaignBriefCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2, 2, 0, 2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.02
  )}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
  [theme.breakpoints.up("lg")]: {
    margin: 0,
    marginBottom: theme.spacing(2),
  },
}));

// Enhanced Video Card Component
const DeliverableVideoCard = ({
  videoData,
  crosspostData = [],
  onLinkChange,
  onCrossLinkChange,
  onSave,
  isSaving,
  hasUnsavedChanges,
  canEdit,
  theme,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const progress = calculateVideoProgress(videoData, crosspostData);

  const getStatusColor = () => {
    switch (progress.status) {
      case "completed":
        return theme.palette.success.main;
      case "in-progress":
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[400];
    }
  };

  const getStatusIcon = (completed, isActive = false) => {
    if (completed)
      return <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />;
    if (isActive)
      return <ScheduleIcon sx={{ fontSize: 16, color: "primary.main" }} />;
    return (
      <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: "grey.400" }} />
    );
  };

  return (
    <Card
      elevation={1}
      sx={{
        mb: 1.5,
        border: `1px solid ${alpha(getStatusColor(), 0.2)}`,
        borderRadius: 2,
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[2],
          borderColor: alpha(getStatusColor(), 0.4),
        },
      }}
    >
      {/* Compact Header */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: alpha(getStatusColor(), 0.03),
          borderBottom: isExpanded
            ? `1px solid ${theme.palette.divider}`
            : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: getStatusColor(),
                fontSize: "0.8rem",
              }}
            >
              {String(videoData.id).padStart(2, "0")}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                {videoData.promotionPlatform}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {videoData.promotionType}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`${Math.round(progress.progressPercentage)}%`}
              size="small"
              color={
                progress.status === "completed"
                  ? "success"
                  : progress.status === "in-progress"
                  ? "primary"
                  : "default"
              }
              sx={{ height: 20, fontSize: "0.65rem", minWidth: 40 }}
            />
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ width: 24, height: 24 }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress.progressPercentage}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.grey[300], 0.3),
            "& .MuiLinearProgress-bar": {
              backgroundColor: getStatusColor(),
              borderRadius: 2,
            },
          }}
        />

        {!isExpanded && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
            {progress.steps.slice(0, 4).map((step) => (
              <Chip
                key={step.id}
                size="small"
                label={step.label.split(" ")[0]}
                variant={step.completed ? "filled" : "outlined"}
                color={step.completed ? "success" : "default"}
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Expanded Content */}
      <Box sx={{ display: isExpanded ? "block" : "none", p: 1.5 }}>
        {/* Submission Link */}
        {videoData.submissionLink && (
          <CompactSection>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
            >
              📤 Content Submission
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => window.open(videoData.submissionLink, "_blank")}
            >
              View Uploaded Files
            </Button>
          </CompactSection>
        )}

        {/* Live Platform Links */}
        {canEdit && (
          <CompactSection>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
            >
              🔗 Published Content
            </Typography>
            <Stack spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Live Link"
                value={videoData.liveLink || ""}
                onChange={(e) =>
                  onLinkChange?.(
                    videoData.id,
                    e.target.value,
                    videoData.boostCode || ""
                  )
                }
                placeholder="Link to published content"
                InputProps={{
                  endAdornment: videoData.liveLink && (
                    <IconButton
                      size="small"
                      onClick={() => window.open(videoData.liveLink, "_blank")}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Boost/Promo Code"
                value={videoData.boostCode || ""}
                onChange={(e) =>
                  onLinkChange?.(
                    videoData.id,
                    videoData.liveLink || "",
                    e.target.value
                  )
                }
                placeholder="Optional promotional code"
              />
            </Stack>
          </CompactSection>
        )}

        {/* Crossposts */}
        {crosspostData.length > 0 && (
          <CompactSection>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "secondary.main" }}
            >
              📱 Crossposts ({crosspostData.length})
            </Typography>
            <Stack spacing={1}>
              {crosspostData.map((crosspost) => (
                <Card
                  key={crosspost.id}
                  variant="outlined"
                  sx={{ p: 1, bgcolor: "grey.50" }}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                  >
                    {crosspost.promotionPlatform} • {crosspost.promotionType}
                  </Typography>
                  <Stack spacing={0.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Crosspost Link"
                      value={crosspost.liveLink || ""}
                      onChange={(e) =>
                        onCrossLinkChange?.(
                          videoData.id,
                          crosspost.id,
                          e.target.value,
                          crosspost.boostCode || ""
                        )
                      }
                      disabled={!canEdit}
                      InputProps={{
                        endAdornment: crosspost.liveLink && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(crosspost.liveLink, "_blank")
                            }
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Boost Code"
                      value={crosspost.boostCode || ""}
                      onChange={(e) =>
                        onCrossLinkChange?.(
                          videoData.id,
                          crosspost.id,
                          crosspost.liveLink || "",
                          e.target.value
                        )
                      }
                      disabled={!canEdit}
                    />
                  </Stack>
                </Card>
              ))}
            </Stack>
          </CompactSection>
        )}

        {/* Save Button */}
        {canEdit && hasUnsavedChanges && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              size="medium"
              onClick={onSave}
              disabled={isSaving}
              startIcon={
                isSaving ? <CircularProgress size={16} /> : <SaveIcon />
              }
              color="primary"
            >
              {isSaving ? "Saving Changes..." : "Save This Video"}
            </Button>
          </Box>
        )}

        {/* Status Details */}
        <Box
          sx={{
            mt: 1,
            p: 1,
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Progress Details:
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
            {progress.steps.map((step) => (
              <Tooltip key={step.id} title={step.description}>
                <Chip
                  size="small"
                  icon={getStatusIcon(step.completed)}
                  label={step.label}
                  variant={step.completed ? "filled" : "outlined"}
                  color={step.completed ? "success" : "default"}
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const AgencyCreatorCampaign = () => {
  const { campaignId, creatorId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { creatorData } = useCreator();

  const [timelineEvents, setTimelineEvents] = useState(undefined);
  const editedEvents = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    brief: false,
    deliverables: false,
  });
  const [mobileActiveAccordion, setMobileActiveAccordion] = useState("details");
  const [mobileChatFullscreen, setMobileChatFullscreen] = useState(false);
  const [liveLink, setLiveLink] = useState([]);
  const [hasUnsavedLinks, setHasUnsavedLinks] = useState(false);
  const [unsavedVideoIds, setUnsavedVideoIds] = useState(new Set());

  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();
  const chatRef = useRef(null);

  const username = creatorData?.creator?.creator || creatorId;
  const displayName = creatorData?.creator?.display_name || username;

  const {
    data: campaign,
    isLoading: campaignLoading,
    error: campaignError,
  } = useQuery({
    queryKey: ["campaigns", campaignId],
    queryFn: () => client.campaigns.fetch(campaignId),
    enabled: !!campaignId && !!creatorId,
    refetchOnWindowFocus: false,
  });

  const creator = useMemo(() => {
    if (!campaign || !username) return undefined;
    return campaign.creators.find((c) => c.id === username);
  }, [campaign, username]);

  const isValid = !!creator;

  const shouldShowPitchDialog = useMemo(() => {
    if (!campaign || !creator) return false;
    return (
      (creator.status === undefined ||
        creator.status === "Pitched" ||
        creator.status === "Counter") &&
      campaign.campaign_status === "Launched"
    );
  }, [campaign, creator]);

  useEffect(() => {
    if (!campaign || !username) return;

    const fetchTimelineData = async () => {
      try {
        const eventData = await client.campaigns.timeline_by_creator(
          campaignId,
          username
        );
        editedEvents.current = false;
        setTimelineEvents(
          eventData.events.map((e) => ({
            ...e,
            projected_date: new Date(e.projected_date),
            ogStatus: e.status,
          }))
        );
      } catch (err) {
        if (err.response?.status === 404) {
          setTimelineEvents([]);
        } else {
          console.error("Error fetching timeline:", err);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchTimelineData();
  }, [campaign, campaignId, username]);

  const {
    data: conversation,
    isLoading: isConversationLoading,
    refetch: refetchConversation,
  } = useQuery(
    ["campaigns", campaignId, "conversation", username],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaignId,
        creator_id: username,
      }),
    {
      enabled: !!campaign && !!username,
      refetchOnWindowFocus: false,
    }
  );

  const conversationId = conversation?.conversation_id;

  const { mutate: createConversation, isLoading: createConversationLoading } =
    useMutation(client.conversations.createCampaign, {
      onSuccess: () => refetchConversation(),
      onError: (e) =>
        openAlert(
          "Error",
          `Error creating conversation: ${
            e?.response?.data?.error || e.message
          }`
        ),
    });

  useEffect(() => {
    if (creator) {
      const newLiveLinks = creator.videos
        .filter((video) => video.parentId == null)
        .map((video) => ({
          id: video.id,
          promotionType: video.promotionType,
          promotionPlatform: video.promotionPlatform,
          liveLink: video.liveLink || "",
          boostCode: video.boostCode || "",
          submissionLink: video.submissionLink || "",
          draftLink: video.draftLink || "",
          payoutComplete: video.payoutComplete,
          crossposts: creator.videos
            .filter((cross) => cross.parentId === video.id)
            .map((cross) => ({
              id: cross.id,
              promotionType: cross.promotionType,
              promotionPlatform: cross.promotionPlatform,
              liveLink: cross.liveLink || "",
              boostCode: cross.boostCode || "",
            })),
        }));

      setLiveLink(newLiveLinks);
      setHasUnsavedLinks(false);
      setUnsavedVideoIds(new Set());
    }
  }, [creator]);

  const organizedVideos = useMemo(() => {
    return liveLink.map((video) => ({
      ...video,
      crossposts: video.crossposts,
    }));
  }, [liveLink]);

  const overallProgress = useMemo(() => {
    if (!organizedVideos.length)
      return { progressPercentage: 0, completedVideos: 0, totalVideos: 0 };

    let totalSteps = 0;
    let completedSteps = 0;

    organizedVideos.forEach((video) => {
      const progress = calculateVideoProgress(video, video.crossposts);
      totalSteps += progress.totalSteps;
      completedSteps += progress.completedSteps;
    });

    const progressPercentage =
      totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    const completedVideos = organizedVideos.filter((v) => {
      const progress = calculateVideoProgress(v, v.crossposts);
      return progress.status === "completed";
    }).length;

    return {
      progressPercentage,
      completedVideos,
      totalVideos: organizedVideos.length,
      status:
        progressPercentage === 100
          ? "completed"
          : progressPercentage > 0
          ? "in-progress"
          : "not-started",
    };
  }, [organizedVideos]);

  const canEdit =
    campaign?.campaign_status === "Launched" &&
    creator &&
    !["Declined", "Dropped", "Pitched"].includes(creator.status);

  const { mutate: updateTimeline, isLoading: isUpdatingTimeline } = useMutation(
    ({ input }) =>
      client.agency.campaignUpdateTimeline(campaignId, username, input),
    {
      onSuccess: () => {
        openAlert("Success", "Timeline updated.");
        editedEvents.current = false;
      },
      onError: (e) =>
        openAlert(
          "Error",
          `Error updating timeline: ${e.response?.data?.error || e.message}`
        ),
    }
  );

  const { mutate: updateFields, isLoading: isUpdatingFields } = useMutation(
    (input) =>
      client.agency.campaignUpdateFields(campaignId, {
        ...input,
        creator: username,
      }),
    {
      onSuccess: () => {
        openAlert("Success", "Fields updated.");
        setHasUnsavedLinks(false);
        setUnsavedVideoIds(new Set());
      },
      onError: (e) =>
        openAlert(
          "Error",
          `Error updating fields: ${e.response?.data?.error || e.message}`
        ),
    }
  );

  const OnEditTimelineField = (idx, target, value) => {
    editedEvents.current = true;
    setTimelineEvents((prev) =>
      prev.map((ev, i) => (i === idx ? { ...ev, [target]: value } : ev))
    );
  };

  const onSaveChanges = () => {
    if (!editedEvents.current || !timelineEvents) return;
    const foundComplete = timelineEvents.some(
      (ev) => ev.ogStatus !== ev.status && ev.status === TimelineStatus[2]
    );
    if (foundComplete) {
      openAlert(
        "Completed Objective",
        "You're setting an objective as complete. This will notify the manager. Continue?",
        () => updateTimeline({ input: timelineEvents }),
        null,
        "Continue",
        "Cancel"
      );
    } else {
      updateTimeline({ input: timelineEvents });
    }
  };

  const onLinkChange = (id, live, boost) => {
    setLiveLink((prev) =>
      prev.map((v) =>
        v.id === id ? { ...v, liveLink: live, boostCode: boost } : v
      )
    );
    setHasUnsavedLinks(true);
    setUnsavedVideoIds((prev) => new Set(prev).add(id));
  };

  const onCrossLinkChange = (id, crossId, live, boost) => {
    setLiveLink((prev) =>
      prev.map((v) =>
        v.id === id
          ? {
              ...v,
              crossposts: v.crossposts.map((c) =>
                c.id === crossId ? { ...c, liveLink: live, boostCode: boost } : c
              ),
            }
          : v
      )
    );
    setHasUnsavedLinks(true);
    setUnsavedVideoIds((prev) => new Set(prev).add(id));
  };

  const handleSaveSingleVideo = (videoId) => {
    const video = liveLink.find((v) => v.id === videoId);
    if (!video) return;

    updateFields({
      liveLinks: [
        {
          id: video.id,
          liveLink: video.liveLink || null,
          boostCode: video.boostCode || null,
        },
        ...video.crossposts.map((c) => ({
          id: c.id,
          liveLink: c.liveLink || null,
          boostCode: c.boostCode || null,
        })),
      ],
    });
  };

  const handleAssetsSent = () => updateFields({ assetsSent: true });

  const handleExpandToggle = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (isMobile) {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      };

      setVH();
      window.addEventListener("resize", setVH);
      window.addEventListener("orientationchange", setVH);

      return () => {
        window.removeEventListener("resize", setVH);
        window.removeEventListener("orientationchange", setVH);
      };
    }
  }, [isMobile]);

  const handleMobileChatToggle = (open) => {
    setMobileChatFullscreen(open);
    if (open) {
      document.body.style.overflow = "hidden";
      setMobileActiveAccordion("chat");
    } else {
      document.body.style.overflow = "";
      setMobileActiveAccordion("details");
    }
  };

  const handleMobileAccordionChange = (panel) => {
    if (panel === "chat") {
      handleMobileChatToggle(true);
    } else if (panel === "details") {
      if (mobileActiveAccordion === "details") {
        handleMobileChatToggle(true);
      } else {
        setMobileActiveAccordion("details");
      }
    }
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (campaignError) {
      openAlert(
        "Error",
        `Error loading campaign: ${
          campaignError?.response?.data?.error || campaignError.message
        }`,
        () => navigate(".."),
        () => navigate(".."),
        "OK",
        null
      );
    }
  }, [campaignError, openAlert, navigate]);

  useEffect(() => {
    if (campaign && !isValid) {
      openAlert(
        "Error",
        `Creator ${displayName} is not part of this campaign`,
        () => navigate(".."),
        () => navigate(".."),
        "OK",
        null
      );
    }
  }, [campaign, isValid, creatorData, openAlert, navigate]);

  const renderCampaignInfo = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Campaign Header */}
      <InfoSection>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              mb: 0.5,
              textShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {creator?.agencyRate
              ? `$${creator.agencyRate}`
              : creator?.price
              ? `$${creator.price}`
              : "TBD"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Campaign Value
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Tooltip title={getStatusTooltip(campaign?.campaign_status)} arrow>
            <Chip
              label={campaign?.campaign_status}
              color={
                campaign?.campaign_status === "Launched" ? "success" : "default"
              }
              sx={{ cursor: "help", fontWeight: 600 }}
            />
          </Tooltip>
          {creator?.status && (
            <Tooltip title={getStatusTooltip(creator.status)} arrow>
              <Chip
                label={creator.status}
                color={creator.status === "Accepted" ? "success" : "primary"}
                variant="outlined"
                sx={{ cursor: "help" }}
              />
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.3,
          }}
        >
          {campaign?.name}
        </Typography>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1 }}>
          <Chip
            icon={<PersonIcon />}
            label={displayName || username}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<CalendarTodayIcon />}
            label={formatIdealDueDate(campaign?.ideal_duedate)}
            size="small"
            variant="outlined"
          />
        </Box>

        {creator?.assetsSent && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Chip
              icon={<CheckCircleIcon />}
              label="Assets Received ✓"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
      </InfoSection>

      {/* Campaign Brief */}
      <CampaignBriefCard elevation={0}>
        <Accordion
          expanded={expandedSections.brief}
          onChange={() => handleExpandToggle("brief")}
          sx={{ boxShadow: "none", "&:before": { display: "none" } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 48,
              "&.Mui-expanded": { minHeight: 48 },
              px: 2,
              py: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Campaign Brief
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: "text.secondary",
                lineHeight: 1.6,
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.2),
                  borderRadius: 2,
                },
              }}
            >
              {formatMessageContent(campaign?.brief || "No brief provided")}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </CampaignBriefCard>

      {/* Deliverables Section */}
      <Box sx={{ mt: 2 }}>
        <InfoSection sx={{ borderBottom: "none" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AssignmentIcon color="primary" />
              Deliverables
            </Typography>
          </Box>

          {/* Overall Progress */}
          {organizedVideos.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Overall Progress
                </Typography>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                >
                  {overallProgress.completedVideos}/{overallProgress.totalVideos}{" "}
                  Complete
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={overallProgress.progressPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  "& .MuiLinearProgress-bar": {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    borderRadius: 4,
                  },
                }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {Math.round(overallProgress.progressPercentage)}% of all
                deliverables completed
              </Typography>
            </Box>
          )}

          {/* Video Assignments */}
          {organizedVideos.length === 0 ? (
            <Alert severity="info" icon={<AssignmentTurnedInIcon />}>
              <Typography variant="body2">
                Video deliverables will appear here once assigned.
              </Typography>
            </Alert>
          ) : (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <VideoLibraryIcon color="primary" fontSize="small" />
                Video Assignments ({organizedVideos.length})
              </Typography>

              <Stack spacing={0}>
                {organizedVideos.map((video) => (
                  <DeliverableVideoCard
                    key={video.id}
                    videoData={video}
                    crosspostData={video.crossposts}
                    onLinkChange={onLinkChange}
                    onCrossLinkChange={onCrossLinkChange}
                    onSave={() => handleSaveSingleVideo(video.id)}
                    isSaving={isUpdatingFields}
                    hasUnsavedChanges={unsavedVideoIds.has(video.id)}
                    canEdit={canEdit}
                    theme={theme}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Assets Confirmation */}
          {canEdit && !creator?.assetsSent && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "warning.dark", mb: 1 }}
              >
                📋 Campaign Assets
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Confirm when creator has sent all campaign materials.
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleAssetsSent}
                disabled={isUpdatingFields}
                startIcon={<CheckCircleIcon />}
                fullWidth
              >
                Confirm Assets Sent
              </Button>
            </Box>
          )}

          {/* Timeline */}
          {timelineEvents && timelineEvents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <CreatorTimeline
                timelineEvents={timelineEvents}
                onEditField={OnEditTimelineField}
                onSaveChanges={onSaveChanges}
                isSavingChanges={isUpdatingTimeline}
                canEdit={canEdit}
                openAlert={openAlert}
                closeDialog={closeDialog}
              />
            </Box>
          )}
        </InfoSection>
      </Box>
    </Box>
  );

  if (campaignLoading || isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!campaign || !isValid) {
    return null;
  }

  if (shouldShowPitchDialog) {
    return (
      <>
        <PageTitle title={`Campaign: ${campaign?.name} - Agency - Blitz`} />
        <AgencyCampaignPitchDialog
          campaign={campaign}
          openDialog={true}
          username={username}
          handleCloseDialog={() => navigate("..")}
        />
        <AlertDialog alertState={dialogState} />
      </>
    );
  }

  return (
    <>
      <MainContainer>
        <PageTitle title={`Campaign: ${campaign?.name} - Agency - Blitz`} />

        {/* ── Desktop Layout ── */}
        <DesktopChatContainer>
          {/*
            FIX: Remove the CardContent wrapper entirely.
            CardContent adds `padding-bottom: 24px !important` on :last-child
            which breaks flex height. Render renderCampaignInfo() directly inside
            the styled Card so it fills 100% height cleanly.
          */}
          <DesktopSidebar elevation={2}>
            {renderCampaignInfo()}
          </DesktopSidebar>

          <DesktopChatCard elevation={2}>
            {/*
              FIX: Use Box instead of CardContent for the header strip.
              CardContent's :last-child padding override fights flex layout
              and leaves a dead gap at the bottom of the chat panel.
            */}
            <Box
              sx={{
                p: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Discuss campaign details, deliverables, and coordination with{" "}
                  {displayName}
                </Typography>
                <Tooltip
                  title="Use this chat to communicate directly with the creator about campaign questions or updates"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </Tooltip>
              </Box>
            </Box>

            {/* ChatContentWrapper: flex:1 + minHeight:0 fills remaining card height */}
            <ChatContentWrapper>
              {conversationId ? (
                <ChatScreen
                  ref={chatRef}
                  conversationId={conversationId}
                  specificReaderId={username}
                  sx={{
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                  showWarning={false}
                  margin={2}
                  chatSpacing={1}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    gap: 2,
                    py: 6,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                  <Typography variant="h6" color="text.secondary">
                    No conversation started yet
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={!!conversationId || createConversationLoading}
                    onClick={() =>
                      !conversationId &&
                      !createConversationLoading &&
                      createConversation({
                        campaign_id: campaignId,
                        creator_id: username,
                      })
                    }
                  >
                    Start Conversation
                  </Button>
                </Box>
              )}
            </ChatContentWrapper>
          </DesktopChatCard>
        </DesktopChatContainer>

        {/* ── Mobile Layout ── */}
        <MobileChatContainer>
          {/* Mobile Header */}
          <AppBar
            position="static"
            color="default"
            elevation={0}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Toolbar>
              <IconButton edge="start" onClick={() => navigate("..")}>
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flex: 1, ml: 1 }}>
                <Typography variant="h6" noWrap>
                  {campaign?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {displayName}
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          {!mobileChatFullscreen ? (
            <MobileScrollableContent>
              {/* Campaign Details Accordion */}
              <MobileCampaignAccordion
                expanded={mobileActiveAccordion === "details"}
                onChange={() => handleMobileAccordionChange("details")}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 56,
                    "&.Mui-expanded": { minHeight: 56 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <InfoIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Campaign Details
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {campaign?.name} • $
                        {creator?.agencyRate || creator?.price || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    maxHeight: "calc(var(--vh, 1vh) * 100 - 112px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: alpha(theme.palette.text.primary, 0.2),
                      borderRadius: 2,
                    },
                  }}
                >
                  {renderCampaignInfo()}
                </AccordionDetails>
              </MobileCampaignAccordion>

              {/* Campaign Chat Accordion */}
              <MobileCampaignAccordion expanded={false} disableGutters>
                <AccordionSummary
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileAccordionChange("chat");
                  }}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 56,
                    "&.Mui-expanded": { minHeight: 56 },
                    cursor: "pointer",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ChatIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Campaign Chat
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tap to open chat
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
              </MobileCampaignAccordion>
            </MobileScrollableContent>
          ) : (
            <MobileFullscreenChat>
              <MobileChatHeader>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ChatIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Campaign Chat
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {displayName}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => handleMobileChatToggle(false)}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </MobileChatHeader>

              {conversationId ? (
                <ChatScreen
                  ref={chatRef}
                  conversationId={conversationId}
                  specificReaderId={username}
                  sx={{
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                  showWarning={false}
                  margin={1}
                  chatSpacing={1}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    gap: 2,
                    py: 6,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                  <Typography variant="h6" color="text.secondary">
                    No conversation started yet
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={!!conversationId || createConversationLoading}
                    onClick={() =>
                      !conversationId &&
                      !createConversationLoading &&
                      createConversation({
                        campaign_id: campaignId,
                        creator_id: username,
                      })
                    }
                  >
                    Start Conversation
                  </Button>
                </Box>
              )}
            </MobileFullscreenChat>
          )}
        </MobileChatContainer>
      </MainContainer>

      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default AgencyCreatorCampaign;